import { useEffect, useState, type FC } from "react";
import {
  Button,
  Input,
  MaskedInput,
  Modal,
  SelectPicker,
  TagPicker,
} from "rsuite";
import { Product, Tag } from "../../../models/inventory/product";
import { Brand } from "../../../models/inventory/brand";
import { ProductCategory } from "../../../models/inventory/product_category";
import { getBrands } from "../../../services/inventory/BrandApi";
import { getProductCategories } from "../../../services/inventory/ProductCategoryApi";
import CurrencyInput from "react-currency-input-field";
import { addTagProduct, getTags } from "../../../services/inventory/ProductApi";

import CreatableSelect from "react-select/creatable";
import Select, { components, ControlProps } from "react-select";
import { invertColor } from "../../../utils/helper";

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Product) => void;
  product?: Product;
}

const ProductForm: FC<ProductFormProps> = ({
  open,
  onClose,
  onSubmit,
  product,
}) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    []
  );
  const [data, setData] = useState<Product>({
    id: "",
    name: "",
    price: 0,
    prices: [],
    product_images: [],
    length: 10,
    width: 10,
    height: 10,
    weight: 200,
  });

  const getAllBrands = async (search: string) => {
    getBrands({
      page: 1,
      size: 1000,
      search,
    })
      .then((v) => v.json())
      .then((v) => setBrands(v.data.items));

    getTags({ page: 1, size: 1000 })
      .then((v) => v.json())
      .then((v) => setTags(v.data.items));
  };
  const getAllProductCategories = async (search: string) => {
    getProductCategories({
      page: 1,
      size: 10,
      search,
    })
      .then((v) => v.json())
      .then((v) => setProductCategories(v.data.items));
  };
  useEffect(() => {
    if (product) {
      setData(product);
    }
    getAllBrands("");
    getAllProductCategories("");
  }, [product]);

  return (
    <Modal size={"lg"} open={open} onClose={onClose}>
      <Modal.Header>
        <Modal.Title>Product</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="w-full">
          <div className="form-group">
            <label>SKU</label>
            <Input
              name="sku"
              placeholder="SKU"
              value={data.sku}
              onChange={(value, event) => setData({ ...data, sku: value })}
            />
          </div>
          <div className="form-group">
            <label>Barcode</label>
            <Input
              name="barcode"
              placeholder="Barcode"
              value={data.barcode}
              onChange={(value, event) => setData({ ...data, barcode: value })}
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <div className="input">
              <SelectPicker
                data={productCategories}
                name="productCategories"
                value={data.category_id}
                onChange={(value, event) =>
                  setData({ ...data, category_id: value! })
                }
                block
                valueKey="id"
                labelKey="name"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Name</label>
            <Input
              name="name"
              placeholder="Name"
              value={data.name}
              onChange={(value, event) => setData({ ...data, name: value })}
            />
          </div>
          <div className="form-group">
            <label>Price</label>
            <div className="input">
              <CurrencyInput
                className="rs-input"
                value={data.price}
                groupSeparator="."
                decimalSeparator=","
                onValueChange={(value, name, values) =>
                  setData({ ...data, price: values?.float ?? 0 })
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label>Weight</label>
            <div className="input">
              <div className="relative">
                <CurrencyInput
                  className="rs-input"
                  value={data.weight}
                  groupSeparator="."
                  decimalSeparator=","
                  onValueChange={(value, name, values) =>
                    setData({ ...data, weight: values?.float ?? 0 })
                  }
                />
                <small className="absolute bottom-2 right-2">gr</small>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>Dimension (L x W x H)</label>
            <div className="input">
              <div className="grid grid-cols-3 gap-2">
                <div className="relative">
                  <CurrencyInput
                    className="rs-input"
                    value={data.length}
                    groupSeparator="."
                    decimalSeparator=","
                    onValueChange={(value, name, values) =>
                      setData({ ...data, length: values?.float ?? 0 })
                    }
                  />
                  <small className="absolute bottom-2 right-2">cm</small>
                </div>
                <div className="relative">
                  <CurrencyInput
                    className="rs-input"
                    value={data.width}
                    groupSeparator="."
                    decimalSeparator=","
                    onValueChange={(value, name, values) =>
                      setData({ ...data, width: values?.float ?? 0 })
                    }
                  />
                  <small className="absolute bottom-2 right-2">cm</small>
                </div>
                <div className="relative">
                  <CurrencyInput
                    className="rs-input"
                    value={data.height}
                    groupSeparator="."
                    decimalSeparator=","
                    onValueChange={(value, name, values) =>
                      setData({ ...data, height: values?.float ?? 0 })
                    }
                  />
                  <small className="absolute bottom-2 right-2">cm</small>
                </div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>Brand</label>
            <div className="input">
              <SelectPicker
                data={brands}
                name="brand"
                value={data.brand_id}
                onChange={(value, event) =>
                  setData({ ...data, brand_id: value! })
                }
                block
                valueKey="id"
                labelKey="name"
              />
            </div>
          </div>
          {product && (
            <div className="form-group">
              <label>Tag</label>
              <div className="input">
                <CreatableSelect
                  isClearable
                  isMulti
                  value={(data.tags ?? []).map((item) => ({
                    label: item.name,
                    value: item.id,
                    data: item,
                  }))}
                  options={tags.map((item) => ({
                    label: item.name,
                    value: item.id,
                    data: item,
                  }))}
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "100%",
                    }),
                    multiValue(base, props) {
                      return {
                        ...base,
                        backgroundColor: props.data?.data?.color,
                        borderRadius: "10px",
                        padding: "px 10px",
                      };
                    },
                    multiValueLabel(base, props) {
                      return {
                        ...base,
                        color: invertColor(props.data?.data?.color),
                      };
                    },
                  }}
                  onCreateOption={(value) => {
                    addTagProduct(product.id, { name: value })
                      .then((v) => v.json())
                      .then((v) => {
                        setData({
                          ...product,
                          tags: [...(data?.tags ?? []), v.data],
                        });
                        getTags({ page: 1, size: 1000 })
                          .then((v) => v.json())
                          .then((v) => setTags(v.data.items));
                      });
                  }}
                  onChange={(value) => {
                    // console.log(value);
                    setData({
                      ...product,
                      tags: value.map((item) => item.data),
                    });
                  }}
                />
              </div>
            </div>
          )}
          <div className="form-group">
            <label>Description</label>
            <Input
              as={"textarea"}
              rows={5}
              name="description"
              placeholder="Description"
              value={data.description}
              onChange={(value, event) =>
                setData({ ...data, description: value })
              }
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            onClick={() => {
              onSubmit(data!);
              setData({
                id: "",
                name: "",
                price: 0,
                prices: [],
                product_images: [],
                length: 10,
                width: 10,
                height: 10,
                weight: 200,
              });
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default ProductForm;
